import { Navigate, useLocation } from 'react-router-dom'

import { Checkbox } from '@/components/ui/checkbox'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import {
  setSidebarFeatureHidden,
  useHiddenSidebarFeatures,
  useSidebarFeatureCatalog,
  type SidebarFeatureNode,
} from '@/lib/ui-sidebar-features'
import { useAuthStore } from '@/stores/auth-store'

function FeatureRow({
  feature,
  scope,
  hidden,
  nested = false,
}: {
  feature: SidebarFeatureNode
  scope: string
  hidden: ReadonlySet<string>
  nested?: boolean
}) {
  const checked = !hidden.has(feature.key)

  return (
    <label className={`flex items-center gap-3 px-3 py-2 ${nested ? 'pl-9' : ''}`}>
      <Checkbox
        checked={checked}
        onCheckedChange={(value) => setSidebarFeatureHidden(scope, feature.key, value !== true)}
        aria-label={feature.title}
      />
      <span className="text-sm">{feature.title}</span>
    </label>
  )
}

export function SidebarFeatureToggles() {
  const location = useLocation()
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search) || storedServiceInstanceId || 'local'
  const hidden = useHiddenSidebarFeatures(serviceInstanceId)
  const catalog = useSidebarFeatureCatalog()

  return (
    <div className="flex w-full max-w-lg flex-col gap-6">
      {catalog.map((group) => (
        <section key={group.label || 'features'} className="space-y-2">
          {group.label ? (
            <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{group.label}</h2>
          ) : null}
          <div className="divide-y rounded-lg border">
            {group.items.map((item) => (
              <div key={item.key}>
                <FeatureRow feature={item} scope={serviceInstanceId} hidden={hidden} />
                {item.children?.map((child) => (
                  <FeatureRow
                    key={child.key}
                    feature={child}
                    scope={serviceInstanceId}
                    hidden={hidden}
                    nested
                  />
                ))}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

export default function FeaturesSettingsPage() {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  params.set('tab', 'features')
  return <Navigate to={`/job/configuration?${params.toString()}`} replace />
}
